import { cn, generateTextShadow } from "@/lib/utils";
import { t } from "@/lib/i18n";
import { Spinner } from "@/components/ui/spinner";
import { PrizeSparkles } from "@/components/PrizeSparkles";
import { PrizeDisplay } from "./PrizeDisplay";
import { MODEL_REGISTRY } from "@/components/RocketsOverlay";
import { getRewardIcon } from "./missionConstants";
import { RadialStarBurst } from "./RadialStarBurst";
import type { GachaPrizeItem } from "@/lib/types/api";

const WEEK_LABELS = ["W1", "W2", "W3", "W4"];

export function MonthlyBonusTracker({
  streakData,
  onClaim,
  isPending = false,
}: {
  streakData: {
    weeks: boolean[];
    currentWeek: number;
    isComplete: boolean;
    isClaimed: boolean;
    prize: GachaPrizeItem | null;
    reward: { type: string; amount: number };
  };
  onClaim: () => void;
  isPending?: boolean;
}) {
  const canClaim = streakData.isComplete && !streakData.isClaimed;

  return (
    <div className="flex flex-col gap-3">
      <style>{`
        @keyframes prize-pop-shake {
          0%, 100% { transform: scale(1) rotate(0deg); }
          15% { transform: scale(1.18) rotate(-5deg); }
          30% { transform: scale(1.12) rotate(5deg); }
          45% { transform: scale(1.15) rotate(-3deg); }
          60% { transform: scale(1.1) rotate(2deg); }
          75% { transform: scale(1.05) rotate(-1deg); }
        }
      `}</style>

      {/* Title */}
      <h3
        className="text-white font-bold text-sm uppercase"
        style={{ textShadow: generateTextShadow(1) }}
      >
        {t("streakMissions.monthlyStreakBonus")}
      </h3>

      {/* Content: streak boxes left, prize right */}
      <div className="flex items-center justify-between">
        {/* Streak boxes */}
        <div
          className="flex gap-1.5"
          role="list"
          aria-label="Monthly streak progress"
        >
          {streakData.weeks.map((completed, index) => (
            <div
              key={index}
              role="listitem"
              aria-label={`Week ${index + 1}: ${completed ? "completed" : "incomplete"}`}
            >
              {completed ? (
                <PurpleCheckedBox className="size-7.5 sm:size-9" />
              ) : (
                <PurpleUnCheckedBox className="size-7.5 sm:size-9" />
              )}
            </div>
          ))}
        </div>

        {/* Tappable prize display */}
        <button
          type="button"
          disabled={!canClaim || isPending}
          onClick={canClaim && !isPending ? onClaim : undefined}
          className={cn(
            "relative flex flex-col items-center justify-center shrink-0 ml-3 border-0 bg-transparent p-0",
            canClaim && !isPending && "cursor-pointer",
            (!canClaim || isPending) && "cursor-default",
          )}
          aria-label={
            streakData.isClaimed
              ? "Monthly bonus claimed"
              : canClaim
                ? "Tap to claim monthly bonus"
                : "Complete more weeks to claim monthly bonus"
          }
        >
          <RadialStarBurst className="-mt-1" />
          {streakData.prize ? (
            <>
              <PrizeDisplay
                prize={streakData.prize}
                className={cn(
                  "h-14",
                  streakData.isClaimed && "saturate-[.95] brightness-95",
                )}
              />
              <span
                className={cn(
                  "-mt-2 sm:-mt-4 text-xl sm:text-2xl text-white font-bold uppercase leading-tight text-center",
                  streakData.prize.id in MODEL_REGISTRY && "-mt-3",
                )}
                style={{ textShadow: generateTextShadow(1) }}
              >
                {streakData.prize.name}
              </span>
            </>
          ) : (
            <>
              <img
                src={getRewardIcon(streakData.reward.type)}
                className={cn(
                  "h-6 w-auto",
                  streakData.isClaimed && "saturate-[.95] brightness-95",
                )}
                style={{ imageRendering: "pixelated" }}
                alt={streakData.reward.type}
              />

              <span
                className="-mt-2 sm:-mt-4 text-xl sm:text-2xl text-white font-bold uppercase leading-tight text-center"
                style={{ textShadow: generateTextShadow(1) }}
              >
                +{streakData.reward.amount}
              </span>

              {/* Claim tag */}
              {canClaim && !isPending && (
                <span
                  className={cn(
                    "absolute -bottom-3 sm:-bottom-4 left-1/2 -translate-x-1/2 text-[12px] -tracking-tight uppercase whitespace-nowrap",
                    "group-hover:brightness-95 group-hover:saturate-95 group-active:scale-98 px-2 py-0.5 rounded-full z-10",
                    "text-[#7dfe03]",
                  )}
                  style={{
                    animation: "prize-pop-shake 0.85s ease-in-out infinite",
                    textShadow: generateTextShadow(0.35),
                  }}
                >
                  {t("streakMissions.claim")}
                </span>
              )}

              {/* Claimed label */}
              {streakData.isClaimed && (
                <div className="flex flex-col items-center justify-center gap-px absolute -bottom-2 left-1/2 -rotate-8 -translate-x-1/2 font-extrabold uppercase whitespace-nowrap z-10">
                  <svg
                    className="w-7 h-auto"
                    viewBox="0 0 11 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0.647059 2V2.66667H1.94118H2.58824V2H0.647059Z"
                      fill="black"
                    />
                    <path
                      d="M0.647059 2.66667H0V4.66667H0.647059V2.66667Z"
                      fill="black"
                    />
                    <path
                      d="M0.647059 4.66667V5.33333H1.29412V4.66667H0.647059Z"
                      fill="black"
                    />
                    <path
                      d="M1.29412 5.33333V6H1.94118V5.33333H1.29412Z"
                      fill="black"
                    />
                    <path
                      d="M1.94118 6V6.66667H2.58824V6H1.94118Z"
                      fill="black"
                    />
                    <path
                      d="M2.58824 6.66667V7.33333H3.23529V6.66667H2.58824Z"
                      fill="black"
                    />
                    <path
                      d="M3.23529 7.33333V8H5.82353V7.33333H3.23529Z"
                      fill="black"
                    />
                    <path
                      d="M5.82353 7.33333H6.47059V6.66667H5.82353V7.33333Z"
                      fill="black"
                    />
                    <path
                      d="M6.47059 6.66667H7.11765V6H6.47059V6.66667Z"
                      fill="black"
                    />
                    <path
                      d="M7.11765 6H7.76471V5.33333H7.11765V6Z"
                      fill="black"
                    />
                    <path
                      d="M7.76471 5.33333H8.41177V4.66667H7.76471V5.33333Z"
                      fill="black"
                    />
                    <path
                      d="M8.41177 4.66667H9.05882V4H8.41177V4.66667Z"
                      fill="black"
                    />
                    <path
                      d="M9.05882 4H9.70588V3.33333H9.05882V4Z"
                      fill="black"
                    />
                    <path
                      d="M9.05882 0.666667H8.41177V1.33333H9.05882V0.666667Z"
                      fill="black"
                    />
                    <path
                      d="M8.41177 1.33333H7.76471V2H8.41177V1.33333Z"
                      fill="black"
                    />
                    <path
                      d="M7.76471 2H7.11765V2.66667H7.76471V2Z"
                      fill="black"
                    />
                    <path
                      d="M7.11765 2.66667H6.47059V3.33333H7.11765V2.66667Z"
                      fill="black"
                    />
                    <path
                      d="M6.47059 3.33333H5.82353V4H6.47059V3.33333Z"
                      fill="black"
                    />
                    <path
                      d="M3.88235 3.33333H3.23529V4H3.88235V3.33333Z"
                      fill="black"
                    />
                    <path
                      d="M3.23529 2.66667H2.58824V3.33333H3.23529V2.66667Z"
                      fill="black"
                    />
                    <path
                      d="M3.88235 4V4.66667H4.52941V4H3.88235Z"
                      fill="black"
                    />
                    <path
                      d="M3.88235 4.66667V4H3.23529V4.66667H3.88235Z"
                      fill="white"
                    />
                    <path
                      d="M3.23529 4V3.33333H2.58824V4H3.23529Z"
                      fill="white"
                    />
                    <path
                      d="M2.58824 3.33333V2.66667H1.94118V3.33333H2.58824Z"
                      fill="white"
                    />
                    <path
                      d="M4.52941 4.66667H3.88235V5.33333H4.52941V4.66667Z"
                      fill="white"
                    />
                    <path
                      d="M5.17647 4.66667H5.82353V4H5.17647V4.66667Z"
                      fill="black"
                    />
                    <path
                      d="M4.52941 5.33333H5.17647V4.66667H4.52941V5.33333Z"
                      fill="black"
                    />
                    <path
                      d="M9.70588 3.33333H10.3529V2.66667H9.70588V3.33333Z"
                      fill="black"
                    />
                    <path
                      d="M10.3529 2.66667H11V0.666667H10.3529V2.66667Z"
                      fill="black"
                    />
                    <path
                      d="M9.05882 0V0.666667H10.3529V0H9.05882Z"
                      fill="black"
                    />
                    <path
                      d="M0.647059 2.66667V4.66667H1.29412V5.33333H1.94118V6H2.58824V6.66667H3.23529V7.33333H5.82353V6.66667H6.47059V6H7.11765V5.33333H7.76471V4.66667H8.41177V4H9.05882V3.33333H9.70588V2.66667H10.3529V0.666667H9.05882V1.33333H8.41177V2H7.76471V2.66667H7.11765V3.33333H6.47059V4H5.82353V4.66667H5.17647V5.33333H4.52941H3.88235V4.66667H3.23529V4H2.58824V3.33333H1.94118V2.66667H0.647059Z"
                      fill="#7DFE03"
                    />
                  </svg>
                  <span
                    className="text-[#7dfe03] text-xs"
                    style={{ textShadow: generateTextShadow(0.5) }}
                  >
                    {t("streakMissions.claimed")}
                  </span>
                </div>
              )}
            </>
          )}
          {canClaim && !isPending && <PrizeSparkles />}

          {/* Pending spinner overlay */}
          {isPending && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
              <Spinner className="w-5 h-5" />
            </div>
          )}
        </button>
      </div>
    </div>
  );
}

export function PurpleCheckedBox({ className }: { className?: string }) {
  return (
    <svg
      className={`${className} size-6`}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M9 3H4.5V4.5H3V7.5V21H21V3H15H9Z" fill="#4208B5" />
      <path d="M3 1.5V3H1.5V7.5H3V4.5H4.5V3H9V1.5H3Z" fill="#702DF6" />
      <path d="M3 21V22.5H21V21H3Z" fill="#250567" />
      <path
        d="M1.5 21H3V7.5H1.5C1.5 12.7721 1.5 15.7279 1.5 21Z"
        fill="#250567"
      />
      <path d="M21 3V1.5H9V3H15H21Z" fill="#250567" />
      <path d="M21 21H22.5V3H21V21Z" fill="#250567" />
      <path d="M3 22.5V24H21V22.5H3Z" fill="#140336" />
      <path d="M1.5 21V22.5H3V21H1.5Z" fill="#140336" />
      <path d="M3 1.5H1.5V3H3V1.5Z" fill="#140336" />
      <path d="M22.5 3V1.5H21V3H22.5Z" fill="#140336" />
      <path d="M22.5 21H24V3H22.5V21Z" fill="#140336" />
      <path d="M21 22.5H22.5V21H21V22.5Z" fill="#140336" />
      <path d="M3 0V1.5H9H21V0H3Z" fill="#140336" />
      <path d="M1.5 3H0V21H1.5V7.5V3Z" fill="#140336" />
      <path
        d="M4.94118 10.5V11.3333H6.82353H7.76471V10.5H4.94118Z"
        fill="#140336"
      />
      <path d="M4.94118 11.3333H4V13.8333H4.94118V11.3333Z" fill="#140336" />
      <path
        d="M4.94118 13.8333V14.6667H5.88235V13.8333H4.94118Z"
        fill="#140336"
      />
      <path d="M5.88235 14.6667V15.5H6.82353V14.6667H5.88235Z" fill="#140336" />
      <path d="M6.82353 15.5V16.3333H7.76471V15.5H6.82353Z" fill="#140336" />
      <path
        d="M7.76471 16.3333V17.1667H8.70588V16.3333H7.76471Z"
        fill="#140336"
      />
      <path d="M8.70588 17.1667V18H12.4706V17.1667H8.70588Z" fill="#140336" />
      <path
        d="M12.4706 17.1667H13.4118V16.3333H12.4706V17.1667Z"
        fill="#140336"
      />
      <path d="M13.4118 16.3333H14.3529V15.5H13.4118V16.3333Z" fill="#140336" />
      <path d="M14.3529 15.5H15.2941V14.6667H14.3529V15.5Z" fill="#140336" />
      <path
        d="M15.2941 14.6667H16.2353V13.8333H15.2941V14.6667Z"
        fill="#140336"
      />
      <path d="M16.2353 13.8333H17.1765V13H16.2353V13.8333Z" fill="#140336" />
      <path d="M17.1765 13H18.1176V12.1667H17.1765V13Z" fill="#140336" />
      <path
        d="M17.1765 8.83333H16.2353V9.66667H17.1765V8.83333Z"
        fill="#140336"
      />
      <path d="M16.2353 9.66667H15.2941V10.5H16.2353V9.66667Z" fill="#140336" />
      <path d="M15.2941 10.5H14.3529V11.3333H15.2941V10.5Z" fill="#140336" />
      <path
        d="M14.3529 11.3333H13.4118V12.1667H14.3529V11.3333Z"
        fill="#140336"
      />
      <path d="M13.4118 12.1667H12.4706V13H13.4118V12.1667Z" fill="#140336" />
      <path d="M9.64706 12.1667H8.70588V13H9.64706V12.1667Z" fill="#140336" />
      <path
        d="M8.70588 11.3333H7.76471V12.1667H8.70588V11.3333Z"
        fill="#140336"
      />
      <path d="M9.64706 13V13.8333H10.5882V13H9.64706Z" fill="#140336" />
      <path d="M9.64706 13.8333V13H8.70588V13.8333H9.64706Z" fill="#702DF6" />
      <path d="M8.70588 13V12.1667H7.76471V13H8.70588Z" fill="#702DF6" />
      <path
        d="M7.76471 12.1667V11.3333H6.82353V12.1667H7.76471Z"
        fill="#702DF6"
      />
      <path
        d="M10.5882 13.8333H9.64706V14.6667H10.5882V13.8333Z"
        fill="#702DF6"
      />
      <path d="M11.5294 13.8333H12.4706V13H11.5294V13.8333Z" fill="#140336" />
      <path
        d="M10.5882 14.6667H11.5294V13.8333H10.5882V14.6667Z"
        fill="#140336"
      />
      <path
        d="M18.1176 12.1667H19.0588V11.3333H18.1176V12.1667Z"
        fill="#140336"
      />
      <path d="M19.0588 11.3333H20V8.83333H19.0588V11.3333Z" fill="#140336" />
      <path d="M17.1765 8V8.83333H19.0588V8H17.1765Z" fill="#140336" />
      <path
        d="M4.94118 11.3333V13.8333H5.88235V14.6667H6.82353V15.5H7.76471V16.3333H8.70588V17.1667H12.4706V16.3333H13.4118V15.5H14.3529V14.6667H15.2941V13.8333H16.2353V13H17.1765V12.1667H18.1176V11.3333H19.0588V8.83333H17.1765V9.66667H16.2353V10.5H15.2941V11.3333H14.3529V12.1667H13.4118V13H12.4706V13.8333H11.5294V14.6667H10.5882H9.64706V13.8333H8.70588V13H7.76471V12.1667H6.82353V11.3333H4.94118Z"
        fill="#F0FEF5"
      />
    </svg>
  );
}

export function PurpleUnCheckedBox({ className }: { className?: string }) {
  return (
    <svg
      className={`${className} size-6`}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M9 3H4.5V4.5H3V7.5V21H21V3H15H9Z" fill="#3C2863" />
      <path d="M3 1.5V3H1.5V7.5H3V4.5H4.5V3H9V1.5H3Z" fill="#634C90" />
      <path d="M3 21V22.5H21V21H3Z" fill="#19004B" />
      <path
        d="M1.5 21H3V7.5H1.5C1.5 12.7721 1.5 15.7279 1.5 21Z"
        fill="#19004B"
      />
      <path d="M21 3V1.5H9V3H15H21Z" fill="#19004B" />
      <path d="M21 21H22.5V3H21V21Z" fill="#19004B" />
      <path d="M3 22.5V24H21V22.5H3Z" fill="#140336" />
      <path d="M1.5 21V22.5H3V21H1.5Z" fill="#140336" />
      <path d="M3 1.5H1.5V3H3V1.5Z" fill="#140336" />
      <path d="M22.5 3V1.5H21V3H22.5Z" fill="#140336" />
      <path d="M22.5 21H24V3H22.5V21Z" fill="#140336" />
      <path d="M21 22.5H22.5V21H21V22.5Z" fill="#140336" />
      <path d="M3 0V1.5H9H21V0H3Z" fill="#140336" />
      <path d="M1.5 3H0V21H1.5V7.5V3Z" fill="#140336" />
    </svg>
  );
}
